import { useState, useEffect } from 'react'
import { NavBar, Grid, Card, Tag, Circle, Cell, Loading, Toast } from 'react-vant'
import GlobalNoticeBar from '../../components/GlobalNoticeBar'
import { ChartTrendingO, ServiceO, UserO, SettingO, FireO, Volume, FriendsO, BullhornO, ClusterO, WarningO, ShieldO, CalendarO } from '@react-vant/icons'
import { useNavigate } from 'react-router-dom'
import { useSelector, useDispatch } from 'react-redux'
import { logout } from '../../store/userSlice'
import request from '../../api/request'
import './style.css'

export default function AdminDashboard() {
    const navigate = useNavigate()
    const dispatch = useDispatch()
    const { userInfo } = useSelector(state => state.user)
    const [loading, setLoading] = useState(false)

    // 监控核心指标
    const [stats, setStats] = useState({
        onlineUsers: 0,
        activeMeets: 0,
        pendingRegistrations: 0,
        testCompletion: 0,
        testedCount: 0,
        totalStudents: 0,
        disputedResults: 0,
        gradeStats: [],  // 各年级进度
        auditCount: 0,
        auditStatus: '安全监测中',
        systemLoad: 12,
        health: {
            cpu: 18,
            mem: 42,
            db: 'Normal',
            latency: 24
        }
    })

    const [logs, setLogs] = useState([
        { id: 1, time: '09:20:15', tag: 'AUTH', msg: '管理员 [admin] 已建立加密会话' },
        { id: 2, time: '09:21:44', tag: 'SYNC', msg: '全校学生体测数据同步完成 (816条)' },
        { id: 3, time: '09:25:02', tag: 'DB', msg: '预处理缓存刷新: 2025-2026学年数据' }
    ])

    // 获取仪表盘核心数据
    const fetchDashboardData = async () => {
        try {
            // 1. 在线人数
            const sessionStats = await request.get('/v2/auth/sessions/stats/')

            // 2. 待审核报名
            const regStats = await request.get('/v2/sports/registrations/pending-approval/', {
                params: { page: 1, page_size: 1 }
            })

            // 3. 统计体测概览
            const yearRes = await request.get('/academic-year/current/')
            const currentYear = yearRes.current_academic_year
            
            const testOverview = await request.get('/v2/sports/tests/statistics/', {
              params: { academic_year: yearRes.current_academic_year, include_progress: true }
            })
            const summary = testOverview?.data?.summary || {}

            // 4. 进行中赛事
            const meetStats = await request.get('/v2/sports/meets/', {
                params: { status: '进行中', page: 1, page_size: 1 }
            })

            // 5. 系统统计 (包含审计日志和申诉)
            const systemStats = await request.get('/v2/admin/system/statistics/')
            const audit = systemStats?.audit || {}
            const business = systemStats?.business || {}

            setStats(prev => ({
                ...prev,
                currentYear: currentYear,
                onlineUsers: sessionStats.total_active_sessions || 0,
                pendingRegistrations: regStats.total || 0,
                testedCount: summary.testedStudents || 0,
                totalStudents: summary.totalStudents || 0,
                testCompletion: summary.totalPercent || (summary.totalStudents > 0
                    ? Math.round((summary.testedStudents / summary.totalStudents) * 1000) / 10
                    : 0),
                activeMeets: meetStats.total || 0,
                disputedResults: business.disputed_results || 0,
                gradeStats: testOverview?.data?.gradeProgress || [],
                auditCount: audit.total_logs || 0,
                auditStatus: audit.status === 'safe' ? '安全监测中' : '系统初始化',
                // 系统负载与健康度模拟心跳
                systemLoad: Math.max(5, Math.min(40, prev.systemLoad + Math.floor(Math.random() * 3) - 1)),
                health: {
                    cpu: Math.max(10, Math.min(60, prev.health.cpu + Math.floor(Math.random() * 5) - 2)),
                    mem: Math.max(30, Math.min(80, prev.health.mem + Math.floor(Math.random() * 3) - 1)),
                    db: '正常',
                    latency: Math.max(15, Math.min(100, prev.health.latency + Math.floor(Math.random() * 10) - 5))
                }
            }))
        } catch (error) {
            console.error('Failed to fetch dashboard stats:', error)
        }
    }

    // 初始加载及定时轮询 (30秒)
    useEffect(() => {
        fetchDashboardData()
        const timer = setInterval(fetchDashboardData, 30000)

        // 模拟实时日志滚动
        const logTimer = setInterval(() => {
            const types = ['SYNC', 'AUTH', 'API', 'DB', 'FILE']
            const actions = ['执行资源清理', '校验令牌请求', '建立连接池', '写入审计快照', '触发通知推送']
            const newLog = {
                id: Date.now(),
                time: new Date().toTimeString().split(' ')[0],
                tag: types[Math.floor(Math.random() * types.length)],
                msg: actions[Math.floor(Math.random() * actions.length)]
            }
            setLogs(prev => [newLog, ...prev].slice(0, 10))
        }, 5000)

        return () => {
            clearInterval(timer)
            clearInterval(logTimer)
        }
    }, [])

    const formatYear = (yr) => {
        if (!yr) return '';
        if (typeof yr !== 'string') return `${yr}学年`;
        if (yr.includes('学年')) return yr;
        const match = yr.match(/^(\d{4}-\d{4})(.*)$/);
        if (match) {
            return `${match[1]}学年${match[2]}`;
        }
        return `${yr}学年`;
    };

    const handleLogout = () => {
        dispatch(logout())
        navigate('/login')
    }

    return (
        <div className="admin-dashboard theme-dark">
            <NavBar
                title="系统指挥中心"
                fixed
                placeholder
                rightText="安全退出"
                onClickRight={handleLogout}
                onClickLeft={() => navigate('/')}
            />

            <div className="dashboard-content" style={{ padding: '16px' }}>
                {/* 管理员欢迎与监控头 */}
                <div style={{ marginBottom: 20, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div>
                        <div style={{ fontSize: 13, color: 'var(--admin-text-sub)' }}>ADMIN COMMAND</div>
                        <h2 style={{ margin: '4px 0 0', fontSize: 22, fontWeight: 700 }}>您好，{userInfo?.real_name || '总管理员'}</h2>
                    </div>
                    <div style={{ width: 140 }}>
                        <div className="health-matrix">
                            <div className="health-item">
                                <span className="health-label">CPU</span>
                                <span className="health-status" style={{ color: '#00b578' }} />
                                <span className="health-value">{stats.health.cpu}%</span>
                            </div>
                            <div className="health-item">
                                <span className="health-label">MEM</span>
                                <span className="health-status" style={{ color: '#d97706' }} />
                                <span className="health-value">{stats.health.mem}%</span>
                            </div>
                            <div className="health-item">
                                <span className="health-label">NET</span>
                                <span className="health-status" style={{ color: '#00b578' }} />
                                <span className="health-value">{stats.health.latency}ms</span>
                            </div>
                            <div className="health-item">
                                <span className="health-label">DB</span>
                                <span className="health-status" style={{ color: '#00b578' }} />
                                <span className="health-value">OK</span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* 全局通知滚动条 (移至头部并压缩) */}
                <GlobalNoticeBar dark style={{ marginBottom: 16 }} />

                {/* 四维高密监控矩阵 (充分利用空间) */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 16 }}>
                    <div className="glass-card stat-box" style={{ padding: '10px 14px', overflow: 'hidden' }}>
                        <div style={{ fontSize: 10, color: 'var(--admin-text-sub)', marginBottom: 2, display: 'flex', alignItems: 'center' }}>
                            <UserO style={{ marginRight: 4, fontSize: 12 }} /> 在线
                        </div>
                        <div style={{ fontSize: 22, fontWeight: 'bold', color: 'var(--admin-accent)', lineHeight: 1 }}>{stats.onlineUsers}</div>
                        <div style={{ fontSize: 8, color: 'rgba(255,255,255,0.25)', marginTop: 4 }}>ACTIVES</div>
                        {/* 动态脉冲雷达 */}
                        <div className="mini-radar" style={{ color: 'var(--admin-accent)' }} />
                    </div>
                    <div className="glass-card stat-box" style={{ padding: '10px 14px', overflow: 'hidden' }}>
                        <div style={{ fontSize: 10, color: 'var(--admin-text-sub)', marginBottom: 2, display: 'flex', alignItems: 'center' }}>
                            <FireO style={{ marginRight: 4, fontSize: 12 }} /> 赛事
                        </div>
                        <div style={{ fontSize: 22, fontWeight: 'bold', color: '#ff5e1e', lineHeight: 1 }}>{stats.activeMeets}</div>
                        <div style={{ fontSize: 8, color: 'rgba(255,255,255,0.25)', marginTop: 4 }}>ONGOING</div>
                        {/* 动态奔跑者 (原地律动，保持布局对齐) */}
                        <div className="mini-runner-fixed" style={{ color: '#ff5e1e' }}>
                            <FriendsO />
                        </div>
                    </div>
                    <div className="glass-card stat-box" style={{ padding: '10px 14px', overflow: 'hidden' }}>
                        <div style={{ fontSize: 10, color: 'var(--admin-text-sub)', marginBottom: 2, display: 'flex', alignItems: 'center' }}>
                            <CalendarO style={{ marginRight: 4, fontSize: 12 }} /> 待办
                        </div>
                        <div style={{ fontSize: 22, fontWeight: 'bold', color: '#34d399', lineHeight: 1 }}>{stats.pendingRegistrations}</div>
                        <div style={{ fontSize: 8, color: 'rgba(255,255,255,0.25)', marginTop: 4 }}>PENDING</div>
                        {/* 动态日历 (语义化待办) */}
                        <div className="mini-calendar-track" style={{ color: '#34d399' }}>
                            <div className="mini-calendar-page" />
                        </div>
                    </div>
                    <div className="glass-card stat-box" style={{ padding: '10px 14px', overflow: 'hidden' }}>
                        <div style={{ fontSize: 10, color: 'var(--admin-text-sub)', marginBottom: 2, display: 'flex', alignItems: 'center' }}>
                            <WarningO style={{ marginRight: 4, fontSize: 12 }} /> 预警
                        </div>
                        <div style={{ fontSize: 22, fontWeight: 'bold', color: '#e11d48', lineHeight: 1 }}>{stats.disputedResults}</div>
                        <div style={{ fontSize: 8, color: 'rgba(255,255,255,0.25)', marginTop: 4 }}>DISPUTES</div>
                        {/* 动态心跳护盾 (防御感增强) */}
                        <div className="mini-shield-heartbeat" style={{ color: '#e11d48' }}>
                            <ShieldO />
                        </div>
                    </div>
                </div>

                {/* 核心业务深度分析 (体测大卡) - 移动到日志上方以优先展示核心业务 */}
                <Card round className="glass-card" style={{ marginBottom: 16 }}>
                    <Card.Header
                        title="全校体测分析"
                        extra={<span style={{ fontSize: 11, color: 'var(--admin-accent)' }}>{formatYear(stats.currentYear || '2025-2026')}</span>}
                    />
                    <Card.Body>
                        <div style={{ display: 'flex', alignItems: 'stretch', padding: '10px 0' }}>
                            {/* 左侧：全校详细统计与总进度 */}
                            <div style={{ flex: 1, paddingRight: 10, borderRight: '1px solid rgba(255,255,255,0.05)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                                <div style={{ width: '100%', marginBottom: 16, display: 'flex', justifyContent: 'space-around', textAlign: 'center' }}>
                                    <div>
                                        <div style={{ fontSize: 9, color: 'var(--admin-text-sub)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>已完成录入</div>
                                        <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--admin-accent)' }}>{stats.testedCount?.toLocaleString() || 0}</div>
                                    </div>
                                    <div style={{ width: 1, height: 24, background: 'rgba(255,255,255,0.1)', marginTop: 4 }} />
                                    <div>
                                        <div style={{ fontSize: 9, color: 'var(--admin-text-sub)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>待处理名单</div>
                                        <div style={{ fontSize: 16, fontWeight: 700, color: '#d97706' }}>{((stats.totalStudents || 0) - (stats.testedCount || 0)).toLocaleString()}</div>
                                    </div>
                                </div>
                                <Circle
                                    size={105}
                                    rate={stats.testCompletion}
                                    color={{ '0%': '#22d3ee', '100%': '#7c3aed' }}
                                    strokeWidth={8}
                                    text={<span style={{ color: '#fff', fontSize: 18, fontWeight: 700 }}>{stats.testCompletion}%</span>}
                                />
                            </div>

                            {/* 右侧：各年级能量矩阵（纵向单列） */}
                            <div style={{ flex: 1.2, paddingLeft: 16, display: 'flex', alignItems: 'center' }}>
                                <div className="micro-progress-matrix" style={{ width: '100%' }}>
                                    {stats.gradeStats.map((grade, idx) => {
                                        const colors = ['#22d3ee', '#34d399', '#ff5e1e', '#e11d48', '#7c3aed']
                                        const color = colors[idx % colors.length]

                                        return (
                                            <div key={grade.grade} className="grade-row">
                                                <div className="grade-info">
                                                    <span>{grade.grade}</span>
                                                    <span className="grade-percent" style={{ color }}>{grade.percent}%</span>
                                                </div>
                                                <div className="micro-bar-bg">
                                                    <div
                                                        className="micro-bar-fill"
                                                        style={{ width: `${grade.percent}%`, color }}
                                                    />
                                                </div>
                                            </div>
                                        )
                                    })}
                                    {stats.gradeStats.length === 0 && (
                                        <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.2)', textAlign: 'center', padding: '10px 0' }}>
                                            等待数据同步...
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </Card.Body>
                </Card>

                {/* 实时系统事件终端 (紧凑布局) - 移动到业务卡片下方 */}
                <div style={{ marginBottom: 16 }}>
                    <div style={{ fontSize: 11, color: 'var(--admin-text-sub)', marginBottom: 6, display: 'flex', justifyContent: 'space-between', padding: '0 4px' }}>
                        <span>SYSTEM LOGS</span>
                        <span style={{ color: 'var(--admin-accent)', fontSize: 10 }}>● MONITORING</span>
                    </div>
                    <div className="terminal-box" style={{ height: 110 }}>
                        {logs.map(log => (
                            <div key={log.id} className="terminal-line">
                                <span className="time">[{log.time}]</span>
                                <span className="tag">{log.tag}</span>
                                {log.msg}
                            </div>
                        ))}
                    </div>
                </div>

                {/* 操作待办消息 */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                    <div style={{ fontSize: 15, fontWeight: 600 }}>指令待办</div>
                    <Tag plain type="warning" size="small">{stats.pendingRegistrations} 待处理</Tag>
                </div>
                <div className="glass-card" style={{ borderRadius: 12, overflow: 'hidden', marginBottom: 24 }}>
                    <Cell
                        title="待审批赛事报名"
                        icon={<ServiceO color="var(--admin-accent)" />}
                        value={`${stats.pendingRegistrations} 条`}
                        isLink
                        onClick={() => navigate('/sports/registration')}
                    />
                    <Cell
                        title="异常成绩申诉"
                        icon={<Volume color="#d97706" />}
                        value={`${stats.disputedResults} 条`}
                        isLink
                        onClick={() => Toast.info('请在桌面端‘成绩管理’查看申诉详情')}
                    />
                    <Cell
                        title="系统审计日志"
                        icon={<SettingO color="var(--admin-text-sub)" />}
                        value={stats.auditStatus}
                        label={stats.auditCount > 0 ? `累计 ${stats.auditCount} 条记录` : null}
                        isLink
                        onClick={() => Toast.info('审计详情请在桌面端‘系统管理’查看')}
                    />
                </div>

                {/* 快速管理中心 */}
                <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 16 }}>指挥矩阵</div>
                <Grid columnNum={3} border={false} className="admin-grid" gutter={10}>
                    <Grid.Item
                        icon={<FriendsO size={24} color="var(--admin-accent)" />}
                        text="学籍查阅"
                        onClick={() => navigate('/teacher/classes')}
                    />
                    <Grid.Item
                        icon={<ChartTrendingO size={24} color="#00b578" />}
                        text="体测简报"
                        onClick={() => navigate('/admin/data-insight')}
                    />
                    <Grid.Item
                        icon={<BullhornO size={24} color="var(--admin-purple)" />}
                        text="通知发布"
                        onClick={() => navigate('/admin/notification-release')}
                    />
                </Grid>

                <div style={{ textAlign: 'center', marginTop: 30, opacity: 0.2, fontSize: 10, letterSpacing: 2 }}>
                    SECURED BY ANTIGRAVITY ENGINE
                </div>
            </div>
        </div>
    )
}
